import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "coupon",
    "couponAmount",
    "credits",
    "creditsAmount",
    "creditsManager",
    "error",
    "freeShipping",
    "giftCards",
    "giftCardsAmount",
    "giftCardsManager",
    "mobileSaved",
    "mobileSavedAmount",
    "mobileTotal",
    "productCount",
    "publicTotal",
    "saved",
    "savedAmount",
    "specialOffers",
    "specialOffersAmount",
    "specialOffersManager",
    "subtotal",
    "total"
  ]

  connect() {
    this.element.dataset.cartReady = "true"
  }

  disconnect() {
    delete this.element.dataset.cartReady
  }

  async updateQuantity(event) {
    const button = event.currentTarget
    const orderLine = button.closest(".order-detail")
    if (!orderLine || orderLine.getAttribute("aria-busy") === "true") return

    this.setLoading(orderLine, true)
    this.hideError()

    try {
      const data = await this.request(button.dataset.url, "PUT", {
        active_discount: this.hasActivatedDiscounts(),
        order_id: button.dataset.orderId,
        order_line_id: button.dataset.orderLineId,
        special_offers: this.hasSpecialOffer()
      })
      if (data.message) {
        this.showError(data.message)
      } else if (Object.keys(data).length > 0) {
        this.updateOrderLine(orderLine, data.order_line_hash)
        this.updateOrderPrice(data.order_hash)
        await this.updateCart(data.cart_hash)
      }
    } catch {
      this.showFallbackError()
    } finally {
      this.setLoading(orderLine, false)
    }
  }

  async deleteOrderLine(event) {
    const button = event.currentTarget
    const orderLine = button.closest(".order-detail")
    if (!orderLine || orderLine.getAttribute("aria-busy") === "true") return

    this.setLoading(orderLine, true)
    this.hideError()

    try {
      const data = await this.request(button.dataset.url, "DELETE", {
        active_discount: this.hasActivatedDiscounts(),
        special_offers: this.hasSpecialOffer()
      })
      if (data.order_line_not_found) {
        window.location.reload()
      } else if (data.order_exists) {
        orderLine.remove()
        this.updateOrderPrice(data.order_hash)
        await this.updateCart(data.cart_hash)
      } else if (data.cart_exists) {
        orderLine.remove()
        document.querySelector(`#delivery_${button.dataset.orderId}`)?.remove()
        document.querySelector(`#order_shipping_${button.dataset.orderId}`)?.remove()
        await this.updateCart(data.cart_hash)
      } else {
        window.location.assign(button.dataset.redirectUrl)
      }
    } catch {
      this.showFallbackError()
    } finally {
      this.setLoading(orderLine, false)
    }
  }

  async toggleDiscount(event) {
    const checkbox = event.currentTarget
    const previousValue = !checkbox.checked

    this.setDiscountLoading(true)
    this.hideError()

    try {
      const data = await this.request(checkbox.dataset.url, "POST", this.discountParams())
      this.updateCartPrice(data.hash)
      this.updateDiscountManagers()
    } catch {
      checkbox.checked = previousValue
      this.showFallbackError()
    } finally {
      this.setDiscountLoading(false)
    }
  }

  prepareDiscountModal() {
    const promoCode = this.element.querySelector("#promo_code")
    if (promoCode) promoCode.value = ""
    this.hideError()
  }

  async updateCart(data) {
    this.updateProductCount(data)
    this.updateCartPrice(data)
    await this.updateCreditsManager(data)
  }

  updateOrderLine(orderLine, data) {
    orderLine.querySelectorAll(".quantity").forEach(quantity => quantity.textContent = data.quantity)
    orderLine.querySelector(".quantity-less.delete")?.classList.toggle("active", data.quantity <= 1)
    orderLine.querySelector(".quantity-less:not(.delete)")?.classList.toggle("active", data.quantity > 1)
  }

  updateOrderPrice(data) {
    const shippingFees = this.element.querySelector(`#order_shipping_${data.id} .shipping_fees`)
    if (shippingFees) shippingFees.innerHTML = this.currency(data.shipping_fees)
    if (!this.hasFreeShippingTarget) return

    const offerReached = data.subtotal >= data.minimum_for_special_offer
    this.toggle(this.freeShippingTarget, !offerReached)
    if (!offerReached) {
      const amount = data.minimum_for_special_offer - data.subtotal_without_discount
      const remainingAmount = this.freeShippingTarget.querySelector("span")
      if (remainingAmount) remainingAmount.textContent = amount.toFixed(2)
    }
  }

  updateCartPrice(data) {
    if (this.hasPublicTotalTarget) this.publicTotalTarget.innerHTML = this.currency(data.total_public)
    if (this.hasSubtotalTarget) this.subtotalTarget.innerHTML = this.currency(data.subtotal)
    if (this.hasSavedAmountTarget) this.savedAmountTarget.innerHTML = this.currency(data.total_saved)
    if (this.hasTotalTarget) this.totalTarget.innerHTML = this.currency(data.total)
    if (this.hasCouponAmountTarget) this.couponAmountTarget.innerHTML = this.discountCurrency(data.total_coupon_code)
    if (this.hasCreditsAmountTarget) this.creditsAmountTarget.innerHTML = this.discountCurrency(data.total_credit)
    if (this.hasGiftCardsAmountTarget) this.giftCardsAmountTarget.innerHTML = this.discountCurrency(data.total_gift_card)
    if (this.hasSpecialOffersAmountTarget) this.specialOffersAmountTarget.innerHTML = this.discountCurrency(data.total_special_offers)
    if (this.hasMobileSavedAmountTarget) this.mobileSavedAmountTarget.innerHTML = this.currency(data.total_saved)
    if (this.hasMobileTotalTarget) this.mobileTotalTarget.innerHTML = this.currency(data.total)

    if (this.hasCouponTarget) this.toggle(this.couponTarget, data.total_coupon_code > 0)
    if (this.hasCreditsTarget) this.toggle(this.creditsTarget, data.total_credit > 0)
    if (this.hasGiftCardsTarget) this.toggle(this.giftCardsTarget, data.total_gift_card > 0)
    if (this.hasSpecialOffersTarget) this.toggle(this.specialOffersTarget, data.total_special_offers > 0)
    if (this.hasSpecialOffersManagerTarget) this.toggle(this.specialOffersManagerTarget, data.total_special_offers > 0)
    if (this.hasFreeShippingTarget && data.total_special_offers > 0) this.toggle(this.freeShippingTarget, false)
    if (this.hasSavedTarget) this.toggle(this.savedTarget, data.total_saved > 0)
    if (this.hasMobileSavedTarget) this.toggle(this.mobileSavedTarget, data.total_saved > 0)
  }

  updateProductCount(data) {
    if (!this.hasProductCountTarget) return

    const labels = this.productCountTarget.parentElement.dataset
    const label = data.total_product === 1 ? labels.singular : labels.plural
    this.productCountTarget.textContent = `${data.total_product} ${label}`
  }

  async updateCreditsManager(data) {
    if (!this.hasCreditsManagerTarget) return

    const usableCredits = data.credits.filter(credit => {
      const minimumReached = data.subtotal >= credit.minimum_amount
      const saleAllowed = credit.sales.length === 0 || credit.sales.some(saleId => data.sale_ids.includes(saleId))
      return minimumReached && saleAllowed
    })
    const usableCreditIds = new Set(usableCredits.map(credit => credit.id.toString()))
    const usableTotal = usableCredits.reduce((total, credit) => total + credit.value, 0)
    const checkbox = this.creditsCheckbox

    this.creditsManagerTarget.classList.toggle("disabled", usableTotal <= 0)
    checkbox.disabled = usableTotal <= 0
    checkbox.checked = usableTotal > 0
    const amount = this.creditsManagerTarget.querySelector(".total .amount")
    if (amount) amount.innerHTML = this.currency(usableTotal)

    this.creditsManagerTarget.querySelectorAll(".coupon-code-item").forEach(credit => {
      const unavailable = !usableCreditIds.has(credit.dataset.id)
      this.toggle(credit.querySelector(".not-available"), unavailable)
    })

    if (usableTotal > 0 && data.total_credit <= 0) {
      const response = await this.request(checkbox.dataset.url, "POST", this.discountParams())
      this.updateCartPrice(response.hash)
    }
    this.updateDiscountManagers()
  }

  updateDiscountManagers() {
    if (this.hasCreditsManagerTarget) this.creditsManagerTarget.classList.toggle("active", this.creditsCheckbox.checked)
    if (this.hasGiftCardsManagerTarget) this.giftCardsManagerTarget.classList.toggle("active", this.giftCardsCheckbox.checked)
  }

  setLoading(orderLine, loading) {
    orderLine.setAttribute("aria-busy", String(loading))
    orderLine.querySelectorAll(".cart-item-actions button").forEach(button => button.disabled = loading)
  }

  setDiscountLoading(loading) {
    this.discountCheckboxes.forEach(checkbox => {
      const unavailable = checkbox.closest(".credits_manager")?.classList.contains("disabled")
      checkbox.disabled = loading || unavailable
    })
  }

  discountParams() {
    return {
      credits_activated: this.creditsCheckbox?.checked || false,
      gift_cards_activated: this.giftCardsCheckbox?.checked || false
    }
  }

  get creditsCheckbox() {
    return this.element.querySelector("#active_credits[type='checkbox']")
  }

  get giftCardsCheckbox() {
    return this.element.querySelector("#active_gift_cards[type='checkbox']")
  }

  get discountCheckboxes() {
    return [this.creditsCheckbox, this.giftCardsCheckbox].filter(Boolean)
  }

  hasActivatedDiscounts() {
    const couponVisible = this.hasCouponTarget && !this.couponTarget.hidden && this.couponTarget.getClientRects().length > 0
    return Boolean(this.creditsCheckbox?.checked || this.giftCardsCheckbox?.checked || couponVisible)
  }

  hasSpecialOffer() {
    return this.hasFreeShippingTarget || this.hasSpecialOffersTarget
  }

  currency(value) {
    const amount = Number.isFinite(Number(value)) ? Number(value) : 0
    const [integer, decimals] = amount.toFixed(2).split(".")
    const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, "'")
    return `<small>CHF</small> ${formattedInteger}.${decimals}`
  }

  discountCurrency(value) {
    return `<small class="operator">-</small> ${this.currency(value)}`
  }

  toggle(element, visible) {
    if (element) element.style.display = visible ? "" : "none"
  }

  async request(url, method, params) {
    const response = await fetch(url, {
      method,
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: new URLSearchParams(params)
    })
    if (!response.ok) throw new Error(`Cart request failed (${response.status})`)

    return response.json()
  }

  hideError() {
    if (this.hasErrorTarget) this.errorTarget.style.display = "none"
  }

  showFallbackError() {
    if (this.hasErrorTarget) this.showError(this.errorTarget.dataset.fallbackMessage)
  }

  showError(message) {
    if (!this.hasErrorTarget) return

    this.errorTarget.querySelector(".message").textContent = message
    this.errorTarget.style.removeProperty("display")
  }
}
